<template>
    <form-input-layout :active="active" :help-item-code="helpItemCode">
        <template slot="caption">
            {{ caption }}
        </template>
        <template slot="input">
            <div class="input-container">
                <switch-button inline @selected="selectNegation" v-model="negation" :options="negationOptions"/>
                <inline-switch @selected="selectMode" v-model="conditionMode"
                               :options="conditionModeOptions"/>
                <template v-if="conditionMode == 'range'">
                    <div class="inline-text">
                        from
                    </div>
                    <input v-model="startNumberValue" :class="{ 'input-component': true, 'active': active}"
                           :disabled="disabled" @blur="compute" @keydown.enter="compute"/>
                    <div class="inline-text">
                        to
                    </div>
                    <input v-model="endNumberValue" :class="{ 'input-component': true, 'active': active}"
                           :disabled="disabled" @blur="compute" @keydown.enter="compute"/>
                </template>
                <template v-else-if="conditionMode == 'empty'">
                    <div class="inline-text">N/A</div>
                </template>
                <template v-else>
                    <input v-model="startNumberValue" :class="{ 'input-component': true, 'active': active}"
                           :disabled="disabled" @blur="compute" @keydown.enter="compute"/>
                </template>
            </div>
        </template>
    </form-input-layout>
</template>

<script>
import FormInputLayout from "../form/FormInputLayout";
import SimpleButton from "../button/SimpleButton";
import SwitchButton from "../button/SwitchButton";
import InlineSwitch from "../button/InlineSwitch";
import { mapGetters } from "vuex";
import { evaluate } from "mathjs";
import { prepareExpression } from "@/utils/number-input";

export default {
    name: "FilterInputNumber",
    components: {InlineSwitch, SwitchButton, SimpleButton, FormInputLayout},
    props: {
        helpItemCode: String,
        active: Boolean,
        caption: String,
        disabled: Boolean,
        queryItem: String,
        value: Object,
        precision: Number
    },
    computed: {
        ...mapGetters({
            "getPreference": "userPreferences/getPreference"
        }),

        decimalSeparator() {
            return this.getPreference('defaultNumberSeparator');
        },
        thousandSeparator() {
            return this.getPreference('defaultThousandSeparator');
        },

        outputFormat() {
            return `0,0.${'0'.repeat(this.precision)}`;
        },

        conditionModeOptions() {
            return [
                {
                    caption: '=',
                    value: 'eq'
                },
                {
                    caption: '<',
                    value: 'lt'
                },
                {
                    caption: '>',
                    value: 'gt'
                },
                {
                    caption: '~',
                    value: 'around'
                },
                {
                    caption: this.$t('searchQuery.range'),
                    value: 'range'
                },
                {
                    caption: this.$t('searchQuery.isEmpty'),
                    value: 'empty'
                }]
        },
        negationOptions() {
            return [{caption: this.$t("searchQuery.not"), value: "not"}];
        },

        multiplier() {
            return Math.pow(10, this.precision);
        },

    },
    data: function () {
        return {
            startNumberValue: "",
            endNumberValue: "",
            conditionMode: "",
            negation: ""
        }
    },
    methods: {
        evaluateMathExpression(propertyName) {
            const expression = prepareExpression(this[propertyName]).replaceAll(this.thousandSeparator, '').replaceAll(this.decimalSeparator, '.');
            return Math.round(evaluate(expression) * this.multiplier)
        },

        compute() {
            const emittedParamObject = this.transformResult();
            this.$emit('input', emittedParamObject);
            if (!emittedParamObject) {
                this.startNumberValue = null;
                this.endNumberValue = null;
            }
        },


        selectNegation(negValue) {
            this.negation = negValue;
            this.$emit('input', this.transformResult());
        },
        selectMode(mode) {
            this.conditionMode = mode;
            this.$emit('input', this.transformResult());
        },

        transformResult() {

            if (this.conditionMode === 'empty') {
                return {
                    type: 'float',
                    operator: this.conditionMode,
                    value: "",
                    negation: this.negation === "not"
                }
            } else if (this.conditionMode === '') return undefined;


            try {
                const startNumberResult = this.startNumberValue ? this.evaluateMathExpression('startNumberValue') : 0;
                const endNumberResult = this.conditionMode === 'range' ? this.evaluateMathExpression('endNumberValue') : undefined;


                return {
                    type: 'float',
                    operator: this.conditionMode,
                    value: this.conditionMode === 'range' ? `${startNumberResult};${endNumberResult}` : startNumberResult,
                    negation: this.negation === "not"
                }
            } catch (e) {

                return undefined;
            }

        }
    },
    watch: {
        value: {
            handler(newVal, oldVal) {

                if (newVal !== undefined && newVal.value !== undefined && newVal.operator !== "") {
                    this.conditionMode = newVal.operator;
                    this.negation = newVal.negation || newVal.negation === 'not' ? "not" : "";

                    if (this.conditionMode === 'range') {
                        let vals = newVal.value.split(";");

                        this.startNumberValue = this.$numeral(vals[0] / this.multiplier).format(this.outputFormat);

                        if (vals.length === 2)
                            this.endNumberValue = vals[1] !== null ? this.$numeral(vals[1] / this.multiplier).format(this.outputFormat) : '';
                        else
                            this.endNumberValue = ""
                    } else {
                        this.startNumberValue = this.$numeral(newVal.value / this.multiplier).format(this.outputFormat);
                    }
                } else {
                    this.negation = "";
                    this.conditionMode = "";
                    this.startNumberValue = "";
                    this.endNumberValue = "";
                }

            },
            immediate: true
        }

    }
}
</script>

<style scoped>
.input-container {
    display: flex;
    flex-flow: row;
    white-space: nowrap;
    border-bottom: 1px solid var(--input-border-color);
    background-color: var(--input-background-color);
}

.input-component {
    background-color: transparent;
    border: none;
    color: var(--input-font-color);
    padding: 5px 10px 5px 10px;
    outline: none;
    width: 100%;
    font-family: Source Sans Pro;
}

.active {
    color: var(--input-active-font-color);
    background-color: var(--input-active-background-color);
    border: 1px solid var(--input-active-border-color);
}

.required {
    color: var(--accent-color);
}

.inline-text {
    display: flex;
    padding: 0px 5px 0px 5px;
    align-content: center;
    align-items: center;
    justify-content: center;
    justify-items: center;
}
</style>
